import type { SavedSearchFieldsFragment } from './__generated__/saved-searches.zeus';

const DEFAULT_MARKET_FALLBACK = 'phoenix';
const CANONICAL_MARKET_SLUG = /^[a-z]+(?:[-_][a-z]+)*$/;

/** Whether a saved market value is a routing slug rather than a display label. */
export const isCanonicalMarketSlug = (market: string | null | undefined): market is string =>
  !!market && CANONICAL_MARKET_SLUG.test(market);

/**
 * The market of the route a saved search opens. A search without a market slug,
 * such as a drawn area saved away from a market route, opens the fallback market.
 */
export const pickRouteMarket = (savedSearch: SavedSearchFieldsFragment): string => {
  const { market } = savedSearch.location;
  return isCanonicalMarketSlug(market) ? market : DEFAULT_MARKET_FALLBACK;
};

// Mirrors the marketplace path conventions in `src/pages/homes/[market].tsx`:
//   single-zone   -> /homes/zid_<id>
//   market-only   -> /homes/<market>
//   multi-zone    -> /homes/<market or fallback>?zoneIds=...
export const pickPathname = (savedSearch: SavedSearchFieldsFragment): string => {
  const { zoneIds } = savedSearch.location;
  if (zoneIds.length === 1) {
    return `/homes/zid_${zoneIds[0]}`;
  }
  return `/homes/${pickRouteMarket(savedSearch)}`;
};

// A market-level saved search for the market already loaded on the page can be
// applied with an in-page refetch. Zone/polygon searches and cross-market
// switches need fresh SSR data (market shape, coordinates, zone resolution),
// so those callers must hard-navigate instead.
export const canApplySavedSearchInPlace = (
  savedSearch: SavedSearchFieldsFragment,
  marketIdentifier: string | null,
): boolean =>
  !!savedSearch.location.market &&
  savedSearch.location.market === marketIdentifier &&
  savedSearch.location.zoneIds.length === 0 &&
  savedSearch.location.vertices.length === 0;
